// src/pages/agreements/AgreementsAndDeclarations.jsx
import React, { useMemo } from "react";
import { Row, Col, Card, CardBody, CardHeader, Container, Form, Input, Label } from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import withRouter from "../../components/Common/withRouter";
import { useClientSubmitAgreement } from "../../api/auth";
import CustomHeader from "../../components/Auth/CustomHeader";

const DECLARATION_FIELDS = [
  {
    key: "authorizedContract",
    label: "I am authorized to enter into contracts on behalf of the company."
  },
  {
    key: "agreeServiceAgreement",
    label: "I agree to JobArk’s Client Service Agreement / Terms & Conditions."
  },
  {
    key: "consentDataProcessing",
    label: "I consent to the processing of company and personal data per the Privacy Policy."
  },
  {
    key: "insuranceCompliance",
    label: "I confirm our company maintains required insurances and will remain compliant."
  },
  {
    key: "policyCompliance",
    label: "I will comply with JobArk’s policies (cancellation, payment, billing)."
  },
  {
    key: "safeguardingCompliance",
    label: "I will uphold Health & Safety and safeguarding responsibilities for workers on site."
  }
];

const validationSchema = Yup.object({
  email: Yup.string().email("Invalid email").required("Missing email"),
  policyAndDeclaration: Yup.boolean().oneOf([true], "You must agree to continue"),
  declarations: Yup.object(
    DECLARATION_FIELDS.reduce((shape, f) => {
      shape[f.key] = Yup.boolean().oneOf([true], "Required");
      return shape;
    }, {})
  )
});

const ClientAgreement = (props) => {
  document.title = "Agreements & Declarations | JobArk";

  // read ?email=... from /agreements?email=foo@bar.com
  const search = props?.router?.location?.search || "";
  const qp = useMemo(() => new URLSearchParams(search), [search]);
  const email = qp.get("email") || "";

  const submitAgreement = useClientSubmitAgreement();

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email,
      policyAndDeclaration: false,
      declarations: {
        authorizedContract: false,
        agreeServiceAgreement: false,
        consentDataProcessing: false,
        insuranceCompliance: false,
        policyCompliance: false,
        safeguardingCompliance: false
      }
    },
    validationSchema,
    onSubmit: (values) => {
      // Send payload in the required shape
      const payload = {
        email: values.email,
        policyAndDeclaration: values.policyAndDeclaration,
        declarations: { ...values.declarations }
      };

      submitAgreement.mutate(payload, {
        onSuccess: () => {
          toastr.success("Agreement saved");
        }
      });
    }
  });

  // helpers
  const wasSubmitted = formik.submitCount > 0;
  const invalidAgree =
    (formik.touched.policyAndDeclaration || wasSubmitted) && !!formik.errors.policyAndDeclaration;

  const isSubmitDisabled =
    submitAgreement.isPending ||
    !formik.values.email ||
    !formik.values.policyAndDeclaration ||
    Object.values(formik.values.declarations).some((v) => !v);

  return (
    <>
      <CustomHeader />
      <div className="account-pages my-5 pt-sm-5">
        <Container>
          <Row className="justify-content-center">
            <Col md={10} lg={8} xl={7}>
              <Card className="overflow-hidden">
                <CardHeader>
                  <h5 className="mb-0">
                    {submitAgreement.isSuccess
                      ? "Confirmation Screen"
                      : "Agreements & Declarations"}
                  </h5>
                </CardHeader>
                <CardBody className="pt-0">
                  <div className="p-2">
                    {/* ✅ If already submitted, show confirmation instead of form */}
                    {submitAgreement.isSuccess ? (
                      <div className="confirmation-screen">
                        <p>
                          Hi <strong>{formik.values.email}</strong>,
                        </p>
                        <p>
                          Thank you for submitting your form. <br />
                          We’ve successfully received it, and our team is currently reviewing the
                          details you provided. <br />
                          Once the review is complete, we’ll notify you via email regarding the
                          status and any next steps.
                        </p>
                        <p>
                          If we require any additional information, we’ll reach out to you directly.
                          <br />
                          In the meantime, please feel free to contact us if you have any questions
                          or need further assistance.
                        </p>
                        <p>Thank you</p>
                      </div>
                    ) : (
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          formik.handleSubmit();
                          return false;
                        }}
                      >
                        <div className="mb-4">
                          <div className="small text-muted">
                            <strong>Email:</strong>{" "}
                            {formik.values.email || <span className="text-danger">missing</span>}
                          </div>
                        </div>

                        <div className="mb-3">
                          <h6 className="mb-3">Please review and confirm each item:</h6>

                          {/* granular declaration checkboxes */}
                          <ul className="list-unstyled mb-3">
                            {DECLARATION_FIELDS.map(({ key, label }) => {
                              const touched = formik.touched.declarations?.[key] || wasSubmitted;
                              const error = formik.errors.declarations?.[key];
                              const invalid = !!error && touched;

                              return (
                                <li className="mb-2" key={key}>
                                  <div className="form-check">
                                    <Input
                                      id={key}
                                      className={`form-check-input ${invalid ? "is-invalid" : ""}`}
                                      type="checkbox"
                                      name={`declarations.${key}`}
                                      checked={formik.values.declarations[key]}
                                      onChange={formik.handleChange}
                                      onBlur={() =>
                                        formik.setFieldTouched(`declarations.${key}`, true)
                                      }
                                    />
                                    <Label className="form-check-label" htmlFor={key}>
                                      {label}
                                    </Label>
                                    {invalid && (
                                      <div className="invalid-feedback d-block">Required</div>
                                    )}
                                  </div>
                                </li>
                              );
                            })}
                          </ul>

                          {/* master confirm toggle */}
                          <div className="form-check">
                            <Input
                              id="agreeAll"
                              className={`form-check-input ${invalidAgree ? "is-invalid" : ""}`}
                              type="checkbox"
                              name="policyAndDeclaration"
                              checked={formik.values.policyAndDeclaration}
                              onChange={formik.handleChange}
                              onBlur={() => formik.setFieldTouched("policyAndDeclaration", true)}
                            />
                            <Label className="form-check-label" htmlFor="agreeAll">
                              I confirm and agree to all the declarations above.
                            </Label>
                          </div>
                          {invalidAgree && (
                            <div className="invalid-feedback d-block">
                              {formik.errors.policyAndDeclaration}
                            </div>
                          )}
                        </div>

                        <div className="mt-4 d-grid">
                          <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={isSubmitDisabled}
                          >
                            {submitAgreement.isPending
                              ? "Submitting..."
                              : "Create My Client Account"}
                          </button>
                        </div>
                      </Form>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </>
  );
};

export default withRouter(ClientAgreement);
